import React, { useEffect, useState, useRef } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  Sparkles, Loader2, Plus, Trash2, CheckCircle2, CalendarPlus, Target,
  ListChecks, MoreVertical, RefreshCw, Circle, Check,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const newStep = (text = '') => ({
  text,
  description: '',
  estimated_minutes: null,
  completed: false,
  added_to_calendar: false,
  added_to_goal_id: null,
});

/* ============================ Step Row ============================ */
const StepRow = ({ step, idx, onChange, onDelete, onAddToCalendar, onAddToGoal, theme, busy }) => {
  const toggleDone = () => onChange({ ...step, completed: !step.completed });
  // Show the description box when there is text already OR when the user clicked "add description"
  const [descOpen, setDescOpen] = React.useState(false);
  const hasDesc = !!(step.description && step.description.trim());

  return (
    <div className="rounded-lg px-2.5 py-2 group transition-colors"
      style={{ background: theme.elevated, border: `1px solid ${theme.border}` }}
      data-testid={`step-row-${idx}`}>
      <div className="flex items-start gap-2">
        {/* Checkbox */}
        <button onClick={toggleDone}
          className="mt-0.5 shrink-0 transition-colors"
          aria-label="Marcar paso"
          data-testid={`step-check-${idx}`}>
          {step.completed
            ? <CheckCircle2 size={16} style={{ color: theme.success }} />
            : <Circle size={16} style={{ color: theme.textMuted }} />}
        </button>

        {/* Editable text */}
        <div className="flex-1 min-w-0">
          <input
            value={step.text}
            onChange={(e) => onChange({ ...step, text: e.target.value })}
            placeholder="Describe el paso..."
            className="bg-transparent outline-none text-sm w-full"
            style={{
              color: step.completed ? theme.textMuted : theme.textPrimary,
              textDecoration: step.completed ? 'line-through' : 'none',
            }}
            data-testid={`step-text-${idx}`}
          />
          <div className="flex flex-wrap items-center gap-2 mt-1 text-[10px]" style={{ color: theme.textMuted }}>
            {step.estimated_minutes ? <span>≈ {step.estimated_minutes} min</span> : null}
            {step.added_to_calendar && <span className="inline-flex items-center gap-1" style={{ color: theme.success }}><Check size={10} /> En calendario</span>}
            {step.added_to_goal_id && <span className="inline-flex items-center gap-1" style={{ color: theme.success }}><Check size={10} /> En una meta</span>}
            {!hasDesc && !descOpen && (
              <button onClick={() => setDescOpen(true)}
                className="underline" style={{ color: theme.textMuted }}
                data-testid={`step-add-desc-${idx}`}>
                + descripción
              </button>
            )}
          </div>
        </div>

        {/* Quick actions */}
        <div className="flex items-center gap-0.5 opacity-70 group-hover:opacity-100 transition-opacity shrink-0">
          <button onClick={onAddToCalendar} disabled={busy || step.added_to_calendar}
            className="p-1.5 rounded disabled:opacity-30"
            style={{ color: theme.textMuted }}
            title="Agregar al calendario"
            data-testid={`step-cal-${idx}`}>
            <CalendarPlus size={12} />
          </button>
          <button onClick={onAddToGoal} disabled={busy || !!step.added_to_goal_id}
            className="p-1.5 rounded disabled:opacity-30"
            style={{ color: theme.textMuted }}
            title="Agregar a una meta"
            data-testid={`step-goal-${idx}`}>
            <Target size={12} />
          </button>
          <button onClick={onDelete}
            className="p-1.5 rounded"
            style={{ color: theme.textMuted }}
            title="Eliminar paso"
            data-testid={`step-del-${idx}`}>
            <Trash2 size={12} />
          </button>
        </div>
      </div>

      {/* Description textarea (visible when hasDesc OR user clicked +descripción) */}
      {(hasDesc || descOpen) && (
        <div className="mt-2 pl-6">
          <textarea
            value={step.description || ''}
            onChange={(e) => onChange({ ...step, description: e.target.value })}
            onBlur={() => { if (!step.description?.trim()) setDescOpen(false); }}
            placeholder="Por qué importa y cómo abordarlo..."
            rows={2}
            maxLength={400}
            className="w-full text-xs px-2 py-1.5 rounded-md outline-none resize-none"
            style={{
              background: theme.bg,
              color: theme.textSecondary,
              border: `1px solid ${theme.border}`,
            }}
            data-testid={`step-desc-${idx}`}
          />
        </div>
      )}
    </div>
  );
};

/* ====================== Single task block (card) ===================== */
const TaskBlock = ({
  block, onPatch, onDelete, onGenerate, onAddStep,
  onAddStepToCalendar, onAskGoalPick, theme, generating, savingStep,
}) => {
  const completed = block.steps?.filter((s) => s.completed).length || 0;
  const total = block.steps?.length || 0;
  const pct = total === 0 ? 0 : Math.round((completed / total) * 100);

  const updateStep = (idx, next) => {
    const newSteps = block.steps.map((s, i) => (i === idx ? next : s));
    onPatch({ steps: newSteps });
  };
  const deleteStep = (idx) => onPatch({ steps: block.steps.filter((_, i) => i !== idx) });

  return (
    <article className="rounded-2xl p-4 flex flex-col gap-3"
      style={{ background: theme.card, border: `1px solid ${theme.border}` }}
      data-testid={`task-block-${block.id}`}>
      {/* Header: editable title + delete */}
      <header className="flex items-start gap-2">
        <input
          value={block.task}
          onChange={(e) => onPatch({ task: e.target.value })}
          placeholder="Título de la tarea..."
          className="flex-1 bg-transparent outline-none text-base font-medium"
          style={{ color: theme.textPrimary }}
          data-testid={`block-title-${block.id}`}
        />
        <button onClick={onDelete}
          className="p-1.5 rounded opacity-60 hover:opacity-100"
          style={{ color: theme.textMuted }}
          aria-label="Eliminar tarea"
          data-testid={`block-delete-${block.id}`}>
          <Trash2 size={14} />
        </button>
      </header>

      {/* Optional context line */}
      <Input
        value={block.context || ''}
        onChange={(e) => onPatch({ context: e.target.value })}
        placeholder="Contexto (opcional): plazos, recursos, restricciones..."
        className="text-xs h-8"
        style={{ background: theme.elevated, color: theme.textPrimary, borderColor: theme.border }}
        data-testid={`block-context-${block.id}`}
      />

      {/* Progress bar */}
      {total > 0 && (
        <div className="flex items-center gap-2">
          <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ background: theme.elevated }}>
            <div className="h-full transition-all" style={{ width: `${pct}%`, background: theme.success }} />
          </div>
          <span className="text-[10px] tabular-nums" style={{ color: theme.textMuted }}>{completed}/{total}</span>
        </div>
      )}

      {/* Steps */}
      <div className="space-y-1.5">
        {block.steps?.length ? (
          block.steps.map((s, i) => (
            <StepRow
              key={i}
              step={s}
              idx={i}
              theme={theme}
              busy={savingStep === `${block.id}:${i}`}
              onChange={(next) => updateStep(i, next)}
              onDelete={() => deleteStep(i)}
              onAddToCalendar={() => onAddStepToCalendar(block.id, i)}
              onAddToGoal={() => onAskGoalPick(block.id, i)}
            />
          ))
        ) : (
          <p className="text-xs text-center py-2" style={{ color: theme.textMuted }}>
            Aún sin pasos. Genera con IA o añade manualmente.
          </p>
        )}
      </div>

      {/* Block actions */}
      <div className="flex items-center gap-2 pt-1">
        <button onClick={() => onAddStep(block.id)}
          className="flex-1 text-xs px-2.5 py-2 rounded-md inline-flex items-center justify-center gap-1.5"
          style={{ background: theme.elevated, color: theme.textSecondary, border: `1px solid ${theme.border}` }}
          data-testid={`block-add-step-${block.id}`}>
          <Plus size={12} /> Añadir paso
        </button>
        <button onClick={() => onGenerate(block.id)} disabled={generating || !block.task?.trim()}
          className="flex-1 text-xs px-2.5 py-2 rounded-md inline-flex items-center justify-center gap-1.5 disabled:opacity-40"
          style={{ background: theme.accent, color: theme.bg }}
          data-testid={`block-generate-${block.id}`}>
          {generating ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
          {generating ? 'Generando...' : block.steps?.length ? 'Regenerar IA' : 'Sugerir con IA'}
        </button>
      </div>
    </article>
  );
};

/* ======================== Goal picker dialog ========================= */
const GoalPicker = ({ open, onClose, onPick, theme }) => {
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get('/goals')
      .then((r) => setGoals(Array.isArray(r.data) ? r.data : []))
      .catch(() => toast.error('No pude cargar tus metas.'))
      .finally(() => setLoading(false));
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent style={{ background: theme.card, color: theme.textPrimary, borderColor: theme.border }}>
        <DialogHeader><DialogTitle>Elegir meta</DialogTitle></DialogHeader>
        {loading ? (
          <div className="text-center py-6 text-sm" style={{ color: theme.textMuted }}>
            <Loader2 size={16} className="animate-spin inline mr-2" /> Cargando metas...
          </div>
        ) : goals.length === 0 ? (
          <p className="text-sm py-4" style={{ color: theme.textMuted }}>
            Aún no tienes metas creadas. Crea una desde la sección Metas y vuelve aquí.
          </p>
        ) : (
          <div className="space-y-1.5 max-h-72 overflow-y-auto">
            {goals.map((g) => (
              <button key={g.id} onClick={() => onPick(g)}
                className="w-full text-left p-3 rounded-md hover:opacity-80"
                style={{ background: theme.elevated, border: `1px solid ${theme.border}` }}>
                <p className="text-sm font-medium" style={{ color: theme.textPrimary }}>{g.title}</p>
                {g.description && <p className="text-[11px] mt-0.5 line-clamp-2" style={{ color: theme.textSecondary }}>{g.description}</p>}
              </button>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

/* =============================== Page ================================ */
const TaskBreakdown = () => {
  const { theme } = useTheme();
  const { lang } = useLanguage();

  const [blocks, setBlocks] = useState([]); // task breakdowns loaded from server
  const [loading, setLoading] = useState(true);
  const [generatingId, setGeneratingId] = useState(null);
  const [savingStep, setSavingStep] = useState(null);
  const [goalPick, setGoalPick] = useState(null); // {blockId, stepIdx}
  const [filter, setFilter] = useState('all'); // all | active | done

  const saveTimers = useRef({}); // debounce timers per block

  // Initial load
  useEffect(() => {
    let cancelled = false;
    api.get('/task-breakdown')
      .then((r) => { if (!cancelled) setBlocks(Array.isArray(r.data) ? r.data : []); })
      .catch(() => toast.error('No pude cargar tus tareas.'))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, []);

  // Debounced PUT to backend whenever a block changes.
  const queueSave = (blockId, nextBlock) => {
    if (saveTimers.current[blockId]) clearTimeout(saveTimers.current[blockId]);
    saveTimers.current[blockId] = setTimeout(async () => {
      try {
        await api.put(`/task-breakdown/${blockId}`, {
          task: nextBlock.task,
          context: nextBlock.context || '',
          steps: nextBlock.steps || [],
        });
      } catch {
        toast.error('No pude guardar los cambios.');
      }
    }, 600);
  };

  const patchBlock = (id, patch) => {
    setBlocks((prev) => {
      const next = prev.map((b) => {
        if (b.id !== id) return b;
        const merged = { ...b, ...patch };
        queueSave(id, merged);
        return merged;
      });
      return next;
    });
  };

  const addBlock = async (preset = {}) => {
    try {
      const { data } = await api.post('/task-breakdown', {
        task: preset.task || 'Nueva tarea',
        context: preset.context || '',
        steps: preset.steps || [],
      });
      setBlocks((prev) => [data, ...prev]);
      return data;
    } catch {
      toast.error('No pude crear la tarea.');
      return null;
    }
  };

  const deleteBlock = async (id) => {
    if (!window.confirm('¿Eliminar esta tarea y todos sus pasos?')) return;
    try {
      await api.delete(`/task-breakdown/${id}`);
      setBlocks((prev) => prev.filter((b) => b.id !== id));
    } catch {
      toast.error('No pude eliminar.');
    }
  };

  const addStepToBlock = (id) => {
    setBlocks((prev) => prev.map((b) => {
      if (b.id !== id) return b;
      const merged = { ...b, steps: [...(b.steps || []), newStep('')] };
      queueSave(id, merged);
      return merged;
    }));
  };

  const generateForBlock = async (id) => {
    const block = blocks.find((b) => b.id === id);
    if (!block?.task?.trim()) { toast.error('La tarea no puede estar vacía.'); return; }
    setGeneratingId(id);
    try {
      const { data } = await api.post('/task-breakdown/generate', {
        task: block.task.trim(),
        context: block.context?.trim() || null,
        lang,
      });
      const aiSteps = (data.steps || []).map((s) => ({
        ...newStep(s.text),
        description: s.description || '',
        estimated_minutes: s.estimated_minutes ?? null,
      }));
      // Replace the steps with the AI ones (the previous "Regenerate" semantics)
      patchBlock(id, { steps: aiSteps });
      toast.success(`Generé ${aiSteps.length} paso${aiSteps.length === 1 ? '' : 's'}.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No pude generar los pasos.');
    } finally {
      setGeneratingId(null);
    }
  };

  const addStepToCalendar = async (blockId, stepIdx) => {
    const block = blocks.find((b) => b.id === blockId);
    const step = block?.steps?.[stepIdx];
    if (!step?.text) return;
    setSavingStep(`${blockId}:${stepIdx}`);
    try {
      const now = new Date();
      const start = new Date(now.getTime() + 60 * 60 * 1000);
      start.setMinutes(start.getMinutes() < 30 ? 30 : 0);
      if (start.getMinutes() === 0) start.setHours(start.getHours() + 1);
      const dur = step.estimated_minutes || 30;
      const end = new Date(start.getTime() + dur * 60 * 1000);
      await api.post('/calendar', {
        title: step.text,
        start: start.toISOString(),
        end: end.toISOString(),
        category: 'assignment',
        color: theme.accent,
      });
      const nextSteps = block.steps.map((s, i) => (i === stepIdx ? { ...s, added_to_calendar: true } : s));
      patchBlock(blockId, { steps: nextSteps });
      toast.success('Agregado al calendario.');
    } catch {
      toast.error('No pude agregarlo al calendario.');
    } finally {
      setSavingStep(null);
    }
  };

  const handleGoalPick = async (goal) => {
    const ctx = goalPick;
    setGoalPick(null);
    if (!ctx) return;
    const block = blocks.find((b) => b.id === ctx.blockId);
    const step = block?.steps?.[ctx.stepIdx];
    if (!step?.text) return;
    setSavingStep(`${ctx.blockId}:${ctx.stepIdx}`);
    try {
      const existing = await api.get('/goals');
      const target = (existing.data || []).find((g) => g.id === goal.id) || goal;
      const newSteps = [
        ...(Array.isArray(target.steps) ? target.steps : []),
        { id: (window.crypto?.randomUUID?.() || `s-${Date.now()}`), text: step.text, completed: false },
      ];
      await api.put(`/goals/${goal.id}`, { ...target, steps: newSteps });
      const nextSteps = block.steps.map((s, i) => (i === ctx.stepIdx ? { ...s, added_to_goal_id: goal.id } : s));
      patchBlock(ctx.blockId, { steps: nextSteps });
      toast.success(`Paso agregado a "${target.title}".`);
    } catch {
      toast.error('No pude agregar el paso a la meta.');
    } finally {
      setSavingStep(null);
    }
  };

  const visible = blocks.filter((b) => {
    if (filter === 'all') return true;
    const total = b.steps?.length || 0;
    const done = b.steps?.filter((s) => s.completed).length || 0;
    if (filter === 'active') return total === 0 || done < total;
    if (filter === 'done') return total > 0 && done === total;
    return true;
  });

  const totalSteps = blocks.reduce((s, b) => s + (b.steps?.length || 0), 0);
  const doneSteps = blocks.reduce((s, b) => s + (b.steps?.filter((x) => x.completed).length || 0), 0);

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <header className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <p className="text-[11px] uppercase tracking-[0.25em]" style={{ color: theme.textMuted }}>Tareas</p>
          <h1 className="text-3xl sm:text-4xl font-light tracking-tight mt-1" style={{ color: theme.textPrimary }}>Tu board de tareas</h1>
          <p className="text-sm mt-1" style={{ color: theme.textSecondary }}>
            Crea bloques de tareas con checklists. Pídele a la IA que las desglose o cárgalas manualmente.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-md overflow-hidden" style={{ border: `1px solid ${theme.border}` }}>
            {[
              { id: 'all', label: 'Todas' },
              { id: 'active', label: 'En curso' },
              { id: 'done', label: 'Hechas' },
            ].map((f) => (
              <button key={f.id} onClick={() => setFilter(f.id)}
                className="text-[11px] px-2.5 py-1.5"
                style={{
                  background: filter === f.id ? theme.accent : 'transparent',
                  color: filter === f.id ? theme.bg : theme.textSecondary,
                }}
                data-testid={`filter-${f.id}`}>
                {f.label}
              </button>
            ))}
          </div>
          <Button onClick={() => addBlock({ task: 'Nueva tarea' })}
            className="inline-flex items-center gap-1.5"
            style={{ background: theme.accent, color: theme.bg }}
            data-testid="add-task-block">
            <Plus size={14} /> Nueva tarea
          </Button>
        </div>
      </header>

      {/* Summary strip */}
      {blocks.length > 0 && (
        <div className="rounded-2xl px-4 py-3 flex items-center gap-4"
          style={{ background: theme.card, border: `1px solid ${theme.border}` }}>
          <ListChecks size={18} style={{ color: theme.accent }} />
          <div className="flex-1 text-sm" style={{ color: theme.textSecondary }}>
            <strong style={{ color: theme.textPrimary }}>{blocks.length}</strong> tarea{blocks.length === 1 ? '' : 's'}
            {totalSteps > 0 && (
              <> · <strong style={{ color: theme.textPrimary }}>{doneSteps}/{totalSteps}</strong> pasos completados</>
            )}
          </div>
          {totalSteps > 0 && (
            <div className="hidden sm:block w-40 h-1.5 rounded-full overflow-hidden" style={{ background: theme.elevated }}>
              <div className="h-full transition-all"
                style={{ width: `${Math.round((doneSteps / totalSteps) * 100)}%`, background: theme.success }} />
            </div>
          )}
        </div>
      )}

      {/* Empty / loading state */}
      {loading ? (
        <div className="text-center py-16" style={{ color: theme.textMuted }}>
          <Loader2 size={20} className="animate-spin mx-auto mb-2" /> Cargando tareas...
        </div>
      ) : visible.length === 0 ? (
        <div className="rounded-2xl text-center py-16 px-6"
          style={{ background: theme.card, border: `1px dashed ${theme.border}` }}>
          <Sparkles size={32} className="mx-auto mb-3 opacity-40" style={{ color: theme.textMuted }} />
          <h3 className="text-lg font-medium mb-1" style={{ color: theme.textPrimary }}>
            {blocks.length === 0 ? 'Empieza tu primera tarea' : 'Nada coincide con el filtro'}
          </h3>
          <p className="text-sm mb-5 max-w-md mx-auto" style={{ color: theme.textSecondary }}>
            {blocks.length === 0
              ? 'Crea un bloque de tarea, escribe un título y, si quieres, pulsa "Sugerir con IA" para desglosarla en pasos. También puedes añadir pasos manualmente.'
              : 'Cambia el filtro o crea una nueva tarea para verla aquí.'}
          </p>
          <Button onClick={() => addBlock({ task: 'Nueva tarea' })}
            className="inline-flex items-center gap-1.5"
            style={{ background: theme.accent, color: theme.bg }}>
            <Plus size={14} /> Nueva tarea
          </Button>
        </div>
      ) : (
        // Masonry-like grid: 1 col mobile, 2 col desktop
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
          {visible.map((b) => (
            <TaskBlock
              key={b.id}
              block={b}
              theme={theme}
              generating={generatingId === b.id}
              savingStep={savingStep}
              onPatch={(patch) => patchBlock(b.id, patch)}
              onDelete={() => deleteBlock(b.id)}
              onGenerate={() => generateForBlock(b.id)}
              onAddStep={() => addStepToBlock(b.id)}
              onAddStepToCalendar={addStepToCalendar}
              onAskGoalPick={(blockId, stepIdx) => setGoalPick({ blockId, stepIdx })}
            />
          ))}
        </div>
      )}

      <GoalPicker
        open={!!goalPick}
        onClose={() => setGoalPick(null)}
        onPick={handleGoalPick}
        theme={theme}
      />
    </div>
  );
};

export default TaskBreakdown;
